import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Images,
  ShoppingCart,
  DollarSign,
  TrendingUp,
  Clock,
  ArrowRight,
  Plus,
  Send,
  PiggyBank,
  Percent,
  Upload,
  CheckCircle,
  Settings
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DashboardPage = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hasWatermark, setHasWatermark] = useState(false);
  const [uploadingWatermark, setUploadingWatermark] = useState(false);
  const watermarkInputRef = useRef(null);

  useEffect(() => {
    fetchStats();
    fetchWatermarkStatus();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/dashboard/stats`);
      setStats(response.data);
    } catch (error) {
      toast.error('Failed to load dashboard stats');
    } finally {
      setLoading(false);
    }
  };

  const fetchWatermarkStatus = async () => {
    try {
      const response = await axios.get(`${API}/settings/watermark`);
      setHasWatermark(response.data.has_watermark);
    } catch (error) {
      console.error('Failed to check watermark status');
    }
  };

  const handleWatermarkUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    
    setUploadingWatermark(true);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      await axios.post(`${API}/settings/watermark`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setHasWatermark(true);
      toast.success('Watermark logo uploaded successfully!');
    } catch (error) {
      toast.error('Failed to upload watermark logo');
    } finally {
      setUploadingWatermark(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: 'CAD'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-CA', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-8 w-48 bg-stone-200 rounded" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-32 bg-stone-200 rounded-sm" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-heading text-stone-800">Dashboard</h1>
          <p className="text-stone-600 mt-1">Welcome back! Here's your business overview.</p>
        </div>
        <div className="flex gap-2">
          <Link to="/admin/bulk-email">
            <Button variant="outline" data-testid="bulk-email-btn">
              <Send className="h-4 w-4 mr-2" />
              Send Galleries
            </Button>
          </Link>
          <Link to="/admin/galleries/new">
            <Button className="btn-primary" data-testid="new-gallery-btn">
              <Plus className="h-4 w-4 mr-2" />
              New Gallery
            </Button>
          </Link>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="border-stone-200">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-stone-600">Total Galleries</CardTitle>
            <Images className="h-5 w-5 text-amber-600" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-stone-800">
              {stats?.total_galleries || 0}
            </div>
            <p className="text-xs text-stone-500 mt-1">
              {stats?.published_galleries || 0} published
            </p>
          </CardContent>
        </Card>

        <Card className="border-stone-200">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-stone-600">Total Orders</CardTitle>
            <ShoppingCart className="h-5 w-5 text-amber-600" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-stone-800">
              {stats?.total_orders || 0}
            </div>
            <p className="text-xs text-stone-500 mt-1">
              {stats?.regular_orders || 0} gallery • {stats?.preorders || 0} pre-orders
            </p>
          </CardContent>
        </Card>

        <Card className="border-stone-200">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-stone-600">Paid Orders</CardTitle>
            <TrendingUp className="h-5 w-5 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-stone-800">
              {stats?.paid_orders || 0}
            </div>
            <p className="text-xs text-stone-500 mt-1">
              {stats?.regular_paid || 0} gallery • {stats?.preorders_paid || 0} pre-orders
            </p>
          </CardContent>
        </Card>

        <Card className="border-stone-200">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-stone-600">Total Revenue</CardTitle>
            <DollarSign className="h-5 w-5 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-stone-800 price-display">
              {formatCurrency(stats?.total_revenue || 0)}
            </div>
            <p className="text-xs text-stone-500 mt-1">
              Gallery: {formatCurrency(stats?.orders_revenue || 0)} • Pre-orders: {formatCurrency(stats?.preorders_revenue || 0)}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Profitability Card */}
      <Card className="border-stone-200 bg-gradient-to-r from-green-50 to-emerald-50">
        <CardHeader>
          <CardTitle className="font-heading text-xl text-stone-800 flex items-center gap-2">
            <PiggyBank className="h-5 w-5 text-green-600" />
            Profitability Summary
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div>
              <p className="text-sm text-stone-500 mb-1">Revenue</p>
              <p className="text-2xl font-bold text-stone-800 price-display">
                {formatCurrency(stats?.total_revenue || 0)}
              </p>
            </div>
            <div>
              <p className="text-sm text-stone-500 mb-1">Cost of Goods</p>
              <p className="text-2xl font-bold text-stone-800">
                {formatCurrency(stats?.total_cost || 0)}
              </p>
            </div>
            <div>
              <p className="text-sm text-stone-500 mb-1">Gross Profit</p>
              <p className="text-2xl font-bold text-green-600 price-display">
                {formatCurrency(stats?.gross_profit || 0)}
              </p>
            </div>
            <div>
              <p className="text-sm text-stone-500 mb-1 flex items-center gap-1">
                <Percent className="h-3 w-3" /> Profit Margin
              </p>
              <p className="text-2xl font-bold text-green-600">
                {stats?.profit_margin || 0}%
              </p>
            </div>
          </div>
          {stats?.total_revenue === 0 && (
            <p className="text-sm text-stone-400 mt-4 text-center">
              Profitability data will appear once you have paid orders
            </p>
          )}
        </CardContent>
      </Card>

      {/* Event Profitability */}
      {stats?.event_stats?.length > 0 && (
        <Card className="border-stone-200">
          <CardHeader>
            <CardTitle className="font-heading text-xl text-stone-800 flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-amber-600" />
              Event Profitability
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-stone-200">
                    <th className="text-left py-3 px-2 font-medium text-stone-600">Event</th>
                    <th className="text-right py-3 px-2 font-medium text-stone-600">Orders</th>
                    <th className="text-right py-3 px-2 font-medium text-stone-600">Paid</th>
                    <th className="text-right py-3 px-2 font-medium text-stone-600">Revenue</th>
                    <th className="text-right py-3 px-2 font-medium text-stone-600">Cost</th>
                    <th className="text-right py-3 px-2 font-medium text-stone-600">Profit</th>
                    <th className="text-right py-3 px-2 font-medium text-stone-600">Margin</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.event_stats.map((event) => (
                    <tr key={event.event_id} className="border-b border-stone-100 hover:bg-stone-50">
                      <td className="py-3 px-2 font-medium text-stone-800">{event.event_name}</td>
                      <td className="py-3 px-2 text-right text-stone-600">{event.total_orders}</td>
                      <td className="py-3 px-2 text-right text-stone-600">{event.paid_orders}</td>
                      <td className="py-3 px-2 text-right text-stone-800 font-medium">{formatCurrency(event.revenue)}</td>
                      <td className="py-3 px-2 text-right text-stone-500">{formatCurrency(event.cost)}</td>
                      <td className="py-3 px-2 text-right text-green-600 font-medium">{formatCurrency(event.profit)}</td>
                      <td className="py-3 px-2 text-right">
                        <Badge variant={event.margin >= 50 ? "default" : event.margin >= 30 ? "secondary" : "outline"} 
                               className={event.margin >= 50 ? "bg-green-100 text-green-700" : ""}>
                          {event.margin}%
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Recent Orders */}
      <Card className="border-stone-200">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="font-heading text-xl text-stone-800">Recent Orders</CardTitle>
          <Link to="/admin/orders">
            <Button variant="ghost" size="sm" data-testid="view-all-orders-btn">
              View All <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
          </Link>
        </CardHeader>
        <CardContent>
          {stats?.recent_orders?.length > 0 ? (
            <div className="space-y-4">
              {stats.recent_orders.map((order) => (
                <div 
                  key={order.id}
                  className="flex items-center justify-between p-4 bg-stone-50 rounded-sm"
                >
                  <div className="flex items-center gap-4">
                    <div className={`p-2 rounded border ${order.order_type === 'preorder' ? 'bg-amber-50 border-amber-200' : 'bg-white border-stone-200'}`}>
                      <ShoppingCart className={`h-5 w-5 ${order.order_type === 'preorder' ? 'text-amber-600' : 'text-stone-600'}`} />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-stone-800">{order.customer_name}</p>
                        {order.order_type === 'preorder' && (
                          <Badge className="bg-amber-100 text-amber-700 text-xs">Pre-Order</Badge>
                        )}
                      </div>
                      <p className="text-sm text-stone-500">
                        {order.customer_email}
                        {order.order_type === 'preorder' && order.child_name && ` • ${order.child_name}`}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <p className="font-bold text-stone-800 price-display">
                        {formatCurrency(order.total)}
                      </p>
                      <div className="flex items-center gap-1 text-xs text-stone-500">
                        <Clock className="h-3 w-3" />
                        {formatDate(order.created_at)}
                      </div>
                    </div>
                    <Badge 
                      className={order.payment_status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-stone-100 text-stone-600'}
                    >
                      {order.payment_status}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <ShoppingCart className="h-12 w-12 text-stone-300 mx-auto mb-4" />
              <p className="text-stone-500">No orders yet</p>
              <p className="text-sm text-stone-400 mt-1">Orders will appear here once clients start purchasing</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Link to="/admin/galleries/new" className="block">
          <Card className="border-stone-200 card-hover cursor-pointer h-full">
            <CardContent className="pt-6">
              <Images className="h-8 w-8 text-amber-600 mb-4" />
              <h3 className="font-heading text-lg text-stone-800">Create Gallery</h3>
              <p className="text-sm text-stone-500 mt-1">Add a new client gallery</p>
            </CardContent>
          </Card>
        </Link>
        <Link to="/admin/galleries/bulk" className="block">
          <Card className="border-stone-200 card-hover cursor-pointer h-full">
            <CardContent className="pt-6">
              <TrendingUp className="h-8 w-8 text-amber-600 mb-4" />
              <h3 className="font-heading text-lg text-stone-800">Bulk Import</h3>
              <p className="text-sm text-stone-500 mt-1">Import multiple galleries at once</p>
            </CardContent>
          </Card>
        </Link>
        <Link to="/admin/bulk-email" className="block">
          <Card className="border-stone-200 card-hover cursor-pointer h-full">
            <CardContent className="pt-6">
              <Send className="h-8 w-8 text-amber-600 mb-4" />
              <h3 className="font-heading text-lg text-stone-800">Send Galleries</h3>
              <p className="text-sm text-stone-500 mt-1">Email gallery links in bulk</p>
            </CardContent>
          </Card>
        </Link>
      </div>

      {/* Settings Section */}
      <Card className="border-stone-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5 text-amber-600" />
            Business Settings
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Watermark Logo Upload */}
          <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
            <div>
              <h3 className="font-medium text-stone-800">Watermark Logo</h3>
              <p className="text-sm text-stone-500">
                Upload your logo to be used as a watermark on free digital images
              </p>
              {hasWatermark && (
                <div className="flex items-center gap-1 mt-1 text-green-600 text-sm">
                  <CheckCircle className="h-4 w-4" />
                  Logo uploaded
                </div>
              )}
            </div>
            <div>
              <input
                ref={watermarkInputRef}
                type="file"
                accept="image/*"
                onChange={handleWatermarkUpload}
                className="hidden"
              />
              <Button
                variant="outline"
                onClick={() => watermarkInputRef.current?.click()}
                disabled={uploadingWatermark}
              >
                <Upload className="h-4 w-4 mr-2" />
                {uploadingWatermark ? 'Uploading...' : hasWatermark ? 'Replace Logo' : 'Upload Logo'}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default DashboardPage;
